import { Button, Divider, Flex, Modal, message, Spin } from "antd";
import {
  CarOutlined,
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Typography } from "antd";
import { DeliveryAgenda } from "./deliveryAgenda";
import { useEffect, useState } from "react";
import { axiosInstance } from "@/utilities";
import { API_URL_USER_ADDRESSES_V1 } from "@/services/XCONS_ENV";
import {
  UserAddress,
  UserAddressUpdate,
  UserDeliveryUpdate,
} from "@/types/schema/xconsTypess";
import { QuoteBillingAgendaForm } from "@/pages/quotes/detail/QuoteBillingAgendaForm";
import { toV1Create, toV1Update } from "./agenda.adapter";
const { Text } = Typography;

/** LateralModal no le pone margen al body: las vistas que no lo traen lo ponen acá (xcons-docs#509). */
const MARGEN_DEL_PANEL = "16px 24px";

interface StoreAddresses {
  billingAddresses: UserAddress[];
  deliveryAddresses: UserAddress[];
}

interface Props {
  userAddress: UserAddress[] | null;
  userAddressLoading: boolean;
  updateUserAddress?: () => Promise<void>;
  onTitleChange?: (title: string) => void;
  /** Owner userId; required to create brand-new addresses from the empty state. */
  userId?: number | null;
  /**
   * Store code of the contact's record. Persisted alongside any new address so
   * the row is visible to the contact's address listing (which filters by store).
   */
  storeCode?: string | null;
}

interface State {
  selectedAddress: UserAddress | null;
  editingDeliveryAddress: boolean;
  editingBillingAddress: boolean;
}

export const Agenda = ({
  userAddress,
  userAddressLoading,
  updateUserAddress,
  onTitleChange,
  userId,
  storeCode,
}: Props) => {
  const [loadingAddressUpdate, setLoadingAddressUpdate] = useState(false);
  const [state, setState] = useState<State>({
    selectedAddress: null,
    editingDeliveryAddress: false,
    editingBillingAddress: false,
  });

  const handleEditDeliveryAddress = (address: UserAddress | null) => {
    setState({
      selectedAddress: address,
      editingDeliveryAddress: true,
      editingBillingAddress: false,
    });
  };

  const handleEditBillingAddress = (address: UserAddress | null) => {
    setState({
      selectedAddress: address,
      editingDeliveryAddress: false,
      editingBillingAddress: true,
    });
  };

  useEffect(() => {
    if (onTitleChange && state.editingDeliveryAddress) {
      onTitleChange(
        state.editingDeliveryAddress ? "Edit delivery agenda" : "Agenda",
      );
    }

    if (onTitleChange && state.editingBillingAddress) {
      onTitleChange(
        state.editingBillingAddress ? "Edit billing agenda" : "Agenda",
      );
    }
  }, [
    state.editingDeliveryAddress,
    state.editingBillingAddress,
    onTitleChange,
  ]);

  const handleAddressUpdated = async (updatedAddress: UserAddress) => {
    setLoadingAddressUpdate(true);

    const { userAddressId } = updatedAddress;

    const allowedProperties: UserAddressUpdate = {
      billingTypeId: updatedAddress.billingTypeId,
      // Consumer-Final billings keep the natural-person name on firstname/
      // lastname (split from the form's fullName), so we must persist them
      // alongside the fiscal fields.
      firstname: updatedAddress.firstname,
      lastname: updatedAddress.lastname,
      city: updatedAddress.city,
      company: updatedAddress.company,
      countryId: updatedAddress.countryId,
      latitude: updatedAddress.latitude,
      longitude: updatedAddress.longitude,
      postcode: updatedAddress.postcode,
      region: updatedAddress.region,
      shippingIndication: updatedAddress.shippingIndication,
      street: updatedAddress.street,
      vatId: updatedAddress.vatId,
    };

    const allowedDeliveryProperties: UserDeliveryUpdate = {
      firstname: updatedAddress.firstname,
      lastname: updatedAddress.lastname,
      telephone: updatedAddress.telephone,
      city: updatedAddress.city,
      countryId: updatedAddress.countryId,
      latitude: updatedAddress.latitude,
      longitude: updatedAddress.longitude,
      postcode: updatedAddress.postcode,
      region: updatedAddress.region,
      shippingIndication: updatedAddress.shippingIndication,
      street: updatedAddress.street,
    };

    const payload = state.editingDeliveryAddress
      ? allowedDeliveryProperties
      : allowedProperties;
    const type: "shipping" | "billing" = state.editingDeliveryAddress
      ? "shipping"
      : "billing";

    try {
      if (userAddressId) {
        // Edit existing address.
        await axiosInstance.patch(
          `${API_URL_USER_ADDRESSES_V1}/${userAddressId}`,
          toV1Update(payload),
        );
      } else {
        // Create new address. Requires userId so the row is linked to the
        // owner. `type` is taken from the section the operator was on; in
        // the new schema it determines whether the row goes to the shipping
        // or billing bucket on the next fetch.
        if (!userId) {
          message.error(
            "No se pudo crear la dirección: falta el usuario asociado al contacto.",
          );
          return;
        }
        const effectiveStoreCode =
          (updatedAddress as any)?.stores?.code ??
          (updatedAddress as any)?.store ??
          storeCode ??
          null;
        await axiosInstance.post(
          API_URL_USER_ADDRESSES_V1,
          toV1Create(payload, type, userId, effectiveStoreCode),
        );
      }

      setState({
        selectedAddress: null,
        editingDeliveryAddress: false,
        editingBillingAddress: false,
      });
      message.success(
        userAddressId
          ? "Dirección actualizada"
          : "Dirección agregada a la agenda",
      );
      if (updateUserAddress) {
        updateUserAddress();
      }
    } catch (error) {
      console.error(error);
      message.error("Error al guardar la dirección");
    } finally {
      setLoadingAddressUpdate(false);
    }
  };

  const handleDeleteAddress = (address: UserAddress) => {
    const id = address.userAddressId;
    if (!id) return;
    Modal.confirm({
      title: "¿Eliminar dirección?",
      content: "Esta acción no se puede deshacer.",
      okText: "Eliminar",
      cancelText: "Cancelar",
      okType: "danger",
      onOk: async () => {
        setLoadingAddressUpdate(true);
        try {
          await axiosInstance.delete(`${API_URL_USER_ADDRESSES_V1}/${id}`);
          message.success("Dirección eliminada");
          if (updateUserAddress) {
            await updateUserAddress();
          }
        } catch (error) {
          console.error(error);
          message.error("Error al eliminar la dirección");
        } finally {
          setLoadingAddressUpdate(false);
        }
      },
    });
  };

  const userAddressData = userAddress;

  if (!userAddressData || !Array.isArray(userAddressData)) {
    return <Text style={{ padding: MARGEN_DEL_PANEL, display: "block" }}>No addresses available</Text>;
  }

  const groupedByStore = userAddressData.reduce(
    (acc: Record<string, StoreAddresses>, address: UserAddress) => {
      const storeCode = address.stores?.code ?? "General";
      if (!acc[storeCode]) {
        acc[storeCode] = {
          billingAddresses: [],
          deliveryAddresses: [],
        };
      }

      if (address.addressType.shipping) {
        acc[storeCode].deliveryAddresses.push(address);
      }

      if (address.addressType.billing) {
        acc[storeCode].billingAddresses.push(address);
      }

      return acc;
    },
    {},
  );

  if (state.editingDeliveryAddress) {
    return (
      <div style={{ padding: MARGEN_DEL_PANEL }}>
      <DeliveryAgenda
        address={state.selectedAddress}
        visible={state.editingDeliveryAddress}
        onClose={() =>
          setState({
            ...state,
            editingDeliveryAddress: false,
            editingBillingAddress: false,
          })
        }
        onUpdate={handleAddressUpdated}
      />
      </div>
    );
  }

  if (state.editingBillingAddress) {
    return (
      <div style={{ padding: MARGEN_DEL_PANEL }}>
      <QuoteBillingAgendaForm
        address={state.selectedAddress}
        onClose={() =>
          setState({
            ...state,
            editingDeliveryAddress: false,
            editingBillingAddress: false,
          })
        }
        onUpdate={handleAddressUpdated}
      />
      </div>
    );
  }

  // Reshape: each section iterates stores that have at least one address of
  // its own type. Empty state = both sections collapse to just headers + Add
  // CTA (per Figma node 16368:15187 — no message).
  const billingByStore = Object.entries(groupedByStore).filter(
    ([, addrs]) => addrs.billingAddresses.length > 0,
  );
  const deliveryByStore = Object.entries(groupedByStore).filter(
    ([, addrs]) => addrs.deliveryAddresses.length > 0,
  );

  const sectionAddCta = (onClick: () => void) => (
    <Button
      size="small"
      shape="round"
      icon={<PlusOutlined />}
      onClick={onClick}
    >
      Add
    </Button>
  );

  const addressActions = (
    onEdit: () => void,
    onDelete: () => void,
  ) => (
    <Flex gap={8} style={{ marginTop: 12 }}>
      <Button
        size="small"
        shape="round"
        icon={<EditOutlined />}
        iconPosition="end"
        onClick={onEdit}
        style={{ width: 68 }}
      >
        Edit
      </Button>
      <Button
        size="small"
        shape="circle"
        danger
        icon={<DeleteOutlined />}
        onClick={onDelete}
      />
    </Flex>
  );

  return (
    <Spin spinning={userAddressLoading || loadingAddressUpdate}>
      <Flex vertical style={{ flex: 1, overflow: "hidden" }}>
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
        <Flex vertical gap={20}>
        {/* ===== Billing agenda ===== */}
        <Flex vertical style={{ marginLeft: 2 }} gap={16}>
          <Flex align="center" justify="space-between">
            <Flex gap={8} align="center">
              <FileTextOutlined />
              <Text strong>Billing agenda</Text>
            </Flex>
            {userId && sectionAddCta(() => handleEditBillingAddress(null))}
          </Flex>

          {billingByStore.map(([store, addrs]) => (
            <Flex vertical key={`billing-${store}`} gap={12}>
              {addrs.billingAddresses.map((address) => {
                const fullName = `${address.firstname ?? ""} ${address.lastname ?? ""}`.trim();
                const displayName = address.company || fullName;
                const locationLine = [
                  address.city,
                  address.region,
                  address.postcode ? `(${address.postcode})` : null,
                ]
                  .filter(Boolean)
                  .join(", ");
                return (
                  <Flex
                    vertical
                    key={address.userAddressId}
                    style={{ marginLeft: 22 }}
                    className="agenda-separator"
                  >
                    <Text>{address.billingType?.label ?? "-"}</Text>
                    {displayName && <Text>{displayName}</Text>}
                    {address.vatId && <Text>{address.vatId}</Text>}
                    {address.telephone && <Text>{address.telephone}</Text>}
                    {address.street && <Text>{address.street}</Text>}
                    {locationLine && <Text>{locationLine}</Text>}
                    {address.shippingIndication && (
                      <Text>{address.shippingIndication}</Text>
                    )}
                    {addressActions(
                      () => handleEditBillingAddress(address),
                      () => handleDeleteAddress(address),
                    )}
                  </Flex>
                );
              })}
            </Flex>
          ))}
        </Flex>

        <Divider style={{ margin: 0 }} />

        {/* ===== Delivery agenda ===== */}
        <Flex vertical style={{ marginLeft: 2 }} gap={16}>
          <Flex align="center" justify="space-between">
            <Flex gap={8} align="center">
              <CarOutlined />
              <Text strong>Delivery agenda</Text>
            </Flex>
            {userId && sectionAddCta(() => handleEditDeliveryAddress(null))}
          </Flex>

          {deliveryByStore.map(([store, addrs]) => (
            <Flex vertical key={`delivery-${store}`} gap={12}>
              {addrs.deliveryAddresses.map((address) => {
                const fullName = `${address.firstname ?? ""} ${address.lastname ?? ""}`.trim();
                const locationLine = [
                  address.city,
                  address.region,
                  address.postcode ? `(${address.postcode})` : null,
                ]
                  .filter(Boolean)
                  .join(", ");
                return (
                  <Flex
                    vertical
                    key={address.userAddressId}
                    style={{ marginLeft: 22 }}
                    className="agenda-separator"
                  >
                    {fullName && <Text>{fullName}</Text>}
                    {address.telephone && <Text>{address.telephone}</Text>}
                    {address.street && <Text>{address.street}</Text>}
                    {locationLine && <Text>{locationLine}</Text>}
                    {address.shippingIndication && (
                      <Text>{address.shippingIndication}</Text>
                    )}
                    {addressActions(
                      () => handleEditDeliveryAddress(address),
                      () => handleDeleteAddress(address),
                    )}
                  </Flex>
                );
              })}
            </Flex>
          ))}
        </Flex>

        {!userId && (
          <Text type="secondary" italic style={{ fontSize: 12 }}>
            Falta el usuario asociado al contacto para poder agregar direcciones.
          </Text>
        )}
        </Flex>
        </div>
      </Flex>
    </Spin>
  );
};
